import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "../../config";

/* -------- Helpers d’affichage -------- */
const fmt = (v, suffix = "") => (v ?? v === 0 ? `${v}${suffix}` : "—");

const buildAnnexes = (annexes = {}) => {
  const entries = [
    ["balcon", annexes?.balcon],
    ["cave", annexes?.cave],
    ["cellier", annexes?.cellier],
    ["garage", annexes?.garage],
    ["grenier", annexes?.grenier],
    ["jardin/cour", annexes?.jardinCours],
    ["loggia", annexes?.loggia],
    ["parking", annexes?.parking],
    ["piscine", annexes?.piscine],
    ["terrasse", annexes?.terrasse],
  ]
    .filter(([, n]) => Number(n) > 0)
    .map(([label, n]) => `${n} ${label}`);
  return entries.length ? entries.join(" · ") : "Aucune";
};

// Transforme l’objet “bien” (retour Supabase) → lignes pour tableau
const toRows = (data) => {
  if (!data) return [];
  return [
    { id: "type",        nom: "Type de bien",            valeur: fmt(data.type) },
    { id: "usage",       nom: "Usage",                   valeur: fmt(data.usage) },
    { id: "etat",        nom: "État",                    valeur: fmt(data.etat) },
    { id: "loue",        nom: "Loué",                    valeur: data.loue === "oui" ? "Oui" : "Non" },
    { id: "surface",     nom: "Surface habitable",       valeur: fmt(data.description?.surfaceHabitable, " m²") },
    { id: "pieces",      nom: "Pièces principales",      valeur: fmt(data.description?.nbPiecesPrincipales) },
    { id: "chambres",    nom: "Chambres",                valeur: fmt(data.description?.nbChambresService) },
    { id: "sdb",         nom: "Salles de bain",          valeur: fmt(data.description?.nbSDB) },
    { id: "wc",          nom: "WC",                      valeur: fmt(data.description?.nbWC) },
    { id: "etage",       nom: "Étage",                   valeur: fmt(data.situation?.etage) },
    { id: "ascenseur",   nom: "Ascenseur",               valeur: data.situation?.ascenseur ? "Oui" : "Non" },
    { id: "chauffage",   nom: "Chauffage",               valeur: fmt(data.situation?.chauffage) },
    { id: "adresse",     nom: "Adresse",                 valeur: fmt(data.adresse?.address) },
    { id: "annexes",     nom: "Annexes",                 valeur: buildAnnexes(data.annexes) },
  ];
};

/* --------- Utilitaires export / copie --------- */
function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toCsv(rows) {
  const headers = ["Nom", "Valeur"];
  const lines = [
    headers.join(";"),
    ...rows.map((r) =>
      [r.nom, r.valeur]
        .map((cell) => {
          const s = String(cell ?? "");
          return s.includes(";") || s.includes("\n") || s.includes('"')
            ? `"${s.replace(/"/g, '""')}"`
            : s;
        })
        .join(";")
    ),
  ];
  return lines.join("\n");
}

/* --------- Composant principal --------- */
export default function TabsAffaireBien(args) {
  const transactionId = args?.id; // <- fourni en props
  const [loadingActions, setLoadingActions] = useState(false);
  const [errorActions, setErrorActions] = useState(null);
  const [questionnaireAction, setQuestionnaireAction] = useState(null); // record Airtable trouvé

  const [loadingBien, setLoadingBien] = useState(false);
  const [errorBien, setErrorBien] = useState(null);
  const [rows, setRows] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);

  // 1) Récupère les actions reliées à la transaction
  useEffect(() => {
    if (!transactionId) return;

    (async () => {
      try {
        setLoadingActions(true);
        setErrorActions(null);

        const url = `https://api.airtable.com/v0/appD48APNaGA4GN0B/actions?filterByFormula=SEARCH("${transactionId}",{transaction})`;

        const res = await fetch(url, {
          headers: {
            Authorization:
              "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02",
            Accept: "application/json",
          },
        });

        const json = await res.json();
        const records = Array.isArray(json?.records) ? json.records : [];

        // Trouver l'action "Questionnaire bien"
        const found = records.find((r) => r?.fields?.nom === "Questionnaire bien");
        setQuestionnaireAction(found || null);
      } catch (e) {
        console.error("Erreur chargement actions:", e);
        setErrorActions("Impossible de charger les actions.");
        setQuestionnaireAction(null);
      } finally {
        setLoadingActions(false);
      }
    })();
  }, [transactionId]);

  // 2) Si l’action “Questionnaire bien” est en statut "fait", on charge le bien dans Supabase
  useEffect(() => {
    const statut = questionnaireAction?.fields?.statut;
    const actionId = questionnaireAction?.id;

    if (!actionId) {
      setRows([]);
      setErrorBien(null);
      setLoadingBien(false);
      return;
    }

    if (statut !== "fait") {
      // Pas fait -> on affiche "Données indisponibles"
      setRows([]);
      setErrorBien(null);
      setLoadingBien(false);
      return;
    }

    (async () => {
      try {
        setLoadingBien(true);
        setErrorBien(null);

        // Appelle ton back Supabase
        //https://cloterenotaire.onrender.com/api/questionnaireBien
        //http://localhost:5050/api/questionnaireBien/
        const { data } = await axios.get(
          `${API_BASE}/api/questionnaireBien`
        );

        // Suivant ton back, le bien peut être dans data.bien.bien (objet),
        // sinon dans data.bien
        const bienObj =
          data?.bien?.bien && typeof data.bien.bien === "object"
            ? data.bien.bien
            : data?.bien && typeof data.bien === "object"
            ? data.bien
            : null;

        setRows(toRows(bienObj));
      } catch (e) {
        console.error("Erreur chargement bien:", e);
        setErrorBien("Impossible de charger les informations du bien.");
        setRows([]);
      } finally {
        setLoadingBien(false);
      }
    })();
  }, [questionnaireAction]);

  // Copier uniquement la valeur d’une ligne
  async function copyValue(row) {
    const text = String(row?.valeur ?? "");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedKey(row.id || row.nom);
      setTimeout(() => setCopiedKey(null), 1200);
    } catch (e) {
      console.error("Copie échouée:", e);
    }
  }

  const filenameSuffix = useMemo(
    () => (questionnaireAction?.id ? `_${questionnaireAction.id}` : ""),
    [questionnaireAction?.id]
  );

  const handleExportCSV = () => {
    const csv = toCsv(rows);
    downloadBlob(csv, "text/csv;charset=utf-8", `questionnaire_bien${filenameSuffix}.csv`);
  };

  /* ---------- Rendu ---------- */
  if (!transactionId) {
    return <p className="text-gray-500">Non renseigné par le client.</p>;
  }

  if (loadingActions) {
    return <p className="text-gray-500">Chargement…</p>;
  }

  if (errorActions) {
    return <p className="text-red-600">{errorActions}</p>;
  }

  if (!questionnaireAction) {
    return <p className="text-gray-500">Non renseigné par le client.</p>;
  }

  const statut = questionnaireAction?.fields?.statut;

  if (statut !== "fait") {
    return <p className="text-gray-500">Non renseigné par le client.</p>;
  }

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-sm font-semibold text-gray-700">
          <b>Questionnaire Bien</b>
        </h5>

        <button
          onClick={handleExportCSV}
          className="btn-main"
          disabled={rows.length === 0 || loadingBien}
        >
          Exporter CSV
        </button>
      </div>

      {loadingBien && <p className="text-gray-500">Chargement des informations du bien…</p>}
      {errorBien && <p className="text-red-600">{errorBien}</p>}

      {!loadingBien && !errorBien && rows.length === 0 && (
        <p className="text-gray-500">Aucune donnée à afficher.</p>
      )}

      {!loadingBien && !errorBien && rows.length > 0 && (
        <div className="bg-white rounded-lg p-4  overflow-hidden shadow ring-1 ring-black/5">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Nom
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Valeur
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Copier
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.map((r) => (
                <tr key={r.id || r.nom}>
                  <td className="whitespace-nowrap py-3.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {r.nom}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm text-gray-700">
                    {r.valeur}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm">
                    <button
                      type="button"
                      onClick={() => copyValue(r)}
                      className={`inline-flex items-center rounded-md border px-3 py-1 text-sm
                        ${
                          copiedKey === (r.id || r.nom)
                            ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      title="Copier la valeur"
                    >
                      {copiedKey === (r.id || r.nom) ? "Copié" : "Copier"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}